import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Typography,
  Button,
  CssBaseline,
  IconButton,
  Avatar,
  TextField,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  RestaurantMenu,
  Notifications,
  BarChart,
  ArrowForward,
  ArrowBack,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { collection, doc, setDoc, getDocs, query, where, getDoc } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { debounce } from "lodash"; // Import lodash debounce

// Theme definitions remain unchanged
const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#735DA5", light: "#a583f2" },
    secondary: { main: "#D3C5E5" },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
      loading: "#e4b7ec",
      error: "#f9fafb",
      recommended: "#e5e7eb",
      mostSold: "#f3f4f6",
    },
    text: { primary: "#1A1A2E", secondary: "#362b4dff", tertiary: "#64748b", hero: "#ffffff" },
    error: { main: "#ef4444" },
    custom: {
      cardPrimary: "#E8D7F1",
      cardSecondary: "#F3F4F6",
      accentTeal: "#00C4B4",
      accentPurple: "#D4A5F5",
      contrast: "#00246B",
      heroGradient: "linear-gradient(135deg, rgb(126,114,177), rgb(228,168,240))",
      cardShadow: "0 4px 12px rgba(0,0,0,0.1)",
      cardHoverShadow: "0 8px 16px rgba(0,0,0,0.2)",
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#a583f2", light: "#c4a1ff" },
    secondary: { main: "#CADCFC" },
    background: {
      default: "#1A1A2E",
      paper: "#2A2A40",
      loading: "#1e1e2f",
      error: "#1e1e2f",
      recommended: "#2d2d44",
      mostSold: "#2d2d44",
    },
    text: { primary: "#E5E7EB", secondary: "#FFFFFF", tertiary: "#9ca3af", hero: "#ffffff" },
    error: { main: "#ef4444" },
    custom: {
      cardPrimary: "#3a3a5c",
      cardSecondary: "#2A2A40",
      accentTeal: "#00C4B4",
      accentPurple: "#D4A5F5",
      contrast: "#000612",
      heroGradient: "linear-gradient(135deg, rgba(20, 18, 29, 1), rgba(123, 85, 131, 1))",
      cardShadow: "0 4px 12px rgba(0,0,0,0.5)",
      cardHoverShadow: "0 8px 16px rgba(0,0,0,0.7)",
      buttonPrimary: "#4b5563",
      buttonHover: "#6b7280",
      rating: "#facc15",
    },
  },
});

const LandingPage = ({ darkMode, toggleDarkMode }) => {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [openSignup, setOpenSignup] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    companyCode: "",
    companyAddress: "",
    companyEmail: "",
    companyPhone: "",
    adminUserId: "",
    adminPassword: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const auth = getAuth();

  const carouselImages = [
    "https://cdn.prod.website-files.com/60414b21f1ffcdbb0d5ad688/65206c769c62b75fd6fd0c67_alex-haney-CAhjZmVk5H4-unsplash.jpg",
    "https://images.ctfassets.net/trvmqu12jq2l/6FV4Opt7wUyR91t2FXyOIr/f32972fce10fc87585e831b334ea17ef/header.jpg?q=70&w=1208&h=1080&f=faces&fit=fill",
    "https://popmenucloud.com/cdn-cgi/image/width%3D1200%2Cheight%3D1200%2Cfit%3Dscale-down%2Cformat%3Dauto%2Cquality%3D60/izyapxve/4d84569e-1b1e-4f21-a6f4-0dd16174b4dc.jpg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "reviews"));
        const reviewsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setReviews(reviewsData);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      }
    };
    fetchReviews();
  }, []);

  const addToast = (title, description = "", variant = "default") => {
    const id = Date.now();
    const newToast = { id, title, description, variant };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  };

  const generateBusinessCode = async (name, db) => {
    if (!name) return '';

    // Split the name into words and filter out empty strings
    const words = name.split(/\s+/).filter(word => word.length > 0);
    
    // Initialize prefix
    let prefix = '';

    if (words.length >= 2) {
      // Take first letter of first two words, convert to uppercase, remove non-letters
      prefix = words.slice(0, 2).map(word => word[0].toUpperCase().replace(/[^A-Z]/g, '')).join('');
      // Ensure prefix is 2 or 3 characters, pad with 'X' if needed
      // prefix = prefix.padEnd(3, 'X').slice(0, 3);
    } else {
      // For single word or fewer valid characters, take first 2-3 letters
      const cleanName = name.toUpperCase().replace(/[^A-Z]/g, '');
      if (cleanName.length >= 3) {
        prefix = cleanName.slice(0, 3);
      } else if (cleanName.length === 2) {
        prefix = cleanName;
      } else {
        prefix = cleanName.padEnd(3, 'X');
      }
    }

    // Find next available number
    let counter = 1;
    let uniqueCode = `${prefix}${counter.toString().padStart(3, '0')}`;

    while (true) {
      const companyQuery = query(collection(db, "companies"), where("code", "==", uniqueCode));
      const querySnapshot = await getDocs(companyQuery);
      if (querySnapshot.empty) break;
      counter++;
      uniqueCode = `${prefix}${counter.toString().padStart(3, '0')}`;
    }

    return uniqueCode;
  };

  // Debounced version of generateBusinessCode
  const debouncedGenerateBusinessCode = useCallback(
    debounce(async (name, callback) => {
      const code = await generateBusinessCode(name, db);
      callback(code);
    }, 500), // 500ms debounce delay
    [db]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    if (name === "companyName") {
      // Call debounced function to generate business code
      debouncedGenerateBusinessCode(value, (newCode) => {
        setFormData((prev) => ({ ...prev, companyCode: newCode }));
      });
    }

    setFormData(updatedData);
  };

  const handlePhoneInput = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({ ...prev, companyPhone: value }));
  };

  const validateForm = async () => {
    const errors = {};
    if (!formData.companyName || /[\\/]/.test(formData.companyName)) {
      errors.companyName = "Business name cannot be empty or contain \"/\" or \"\\\".";
    }
    if (!formData.companyCode) {
      errors.companyCode = "Business code cannot be empty.";
    }
    if (!formData.companyAddress) {
      errors.companyAddress = "Address cannot be empty.";
    }
    if (!formData.companyPhone || !/^\d{10}$/.test(formData.companyPhone)) {
      errors.companyPhone = "Phone number must be exactly 10 digits (e.g., 1234567890).";
    }
    if (!formData.companyEmail || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.companyEmail)) {
      errors.companyEmail = "Please enter a valid email address (e.g., example@domain.com).";
    }
    if (!formData.adminUserId || /[\\/!]/.test(formData.adminUserId)) {
      errors.adminUserId = "Admin User ID cannot be empty or contain \"/\" or \"!\".";
    }
    if (!formData.adminPassword) {
      errors.adminPassword = "Password cannot be empty.";
    }
    if (formData.companyCode) {
      const companyQuery = query(collection(db, "companies"), where("code", "==", formData.companyCode));
      const querySnapshot = await getDocs(companyQuery);
      if (!querySnapshot.empty) {
        errors.companyCode = "Business code already exists.";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateForm())) return;

    setLoading(true);
    try {
      const pseudoEmail = `${formData.companyCode}#${formData.adminUserId}@platterly.in`;
      const usersQuery = query(collection(db, "users"), where("userId", "==", `${formData.companyCode}#${formData.adminUserId}`));
      const querySnapshot = await getDocs(usersQuery);
      if (!querySnapshot.empty) {
        setFormErrors({ adminUserId: "Admin User ID already exists for this business." });
        setLoading(false);
        return;
      }

      const companyDocRef = doc(db, "companies", formData.companyCode);
      const companyDoc = await getDoc(companyDocRef);
      if (companyDoc.exists()) {
        setFormErrors({ companyCode: "Business code already exists." });
        setLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, pseudoEmail, formData.adminPassword);
      const user = userCredential.user;

      await setDoc(companyDocRef, {
        name: formData.companyName,
        code: formData.companyCode,
        address: formData.companyAddress,
        email: formData.companyEmail,
        phone: formData.companyPhone,
        admin: { userId: `${formData.companyCode}#${formData.adminUserId}`, uid: user.uid },
        createdAt: new Date().toISOString(),
      });

      await setDoc(doc(db, "users", user.uid), {
        userId: `${formData.companyCode}#${formData.adminUserId}`,
        role: "admin",
        companyId: formData.companyCode,
        createdTime: new Date().toISOString(),
        uid: user.uid,
      });

      addToast("Welcome to RestaurantOS!", "Your free trial has started. Check your email for setup instructions.");
      setFormData({
        companyName: "",
        companyCode: "",
        companyAddress: "",
        companyEmail: "",
        companyPhone: "",
        adminUserId: "",
        adminPassword: "",
      });
      setFormErrors({});
      setOpenSignup(false);
    } catch (err) {
      console.error("Error adding restaurant or admin:", err);
      addToast("Signup Failed", err.message || "Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // The rest of the component (JSX) remains unchanged
  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default }}>
        <CssBaseline />
        <IconButton
          sx={{
            position: "fixed",
            top: 16,
            right: 16,
            color: theme.palette.text.primary,
            bgcolor: theme.palette.background.paper,
            "&:hover": { bgcolor: theme.palette.custom.cardPrimary },
          }}
          onClick={toggleDarkMode}
        >
          {darkMode ? <Brightness7 /> : <Brightness4 />}
        </IconButton>

        <Box
          sx={{
            background: theme.palette.custom.heroGradient,
            py: 12,
            textAlign: "center",
            color: theme.palette.text.hero,
            position: "relative",
            animation: "fadeIn 1s ease-in",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                mb: 4,
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                boxShadow: theme.palette.custom.cardShadow,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.3)",
                  zIndex: 1,
                }}
              />
              <CardMedia
                component="img"
                height="300"
                image={carouselImages[carouselIndex]}
                alt={`App Screenshot ${carouselIndex + 1}`}
                sx={{ objectFit: "cover", width: "100%" }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 16,
                  color: theme.palette.text.hero,
                  bgcolor: "rgba(0,0,0,0.6)",
                  zIndex: 2,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                }}
                onClick={() =>
                  setCarouselIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)
                }
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 16,
                  color: theme.palette.text.hero,
                  bgcolor: "rgba(0,0,0,0.6)",
                  zIndex: 2,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                }}
                onClick={() => setCarouselIndex((prev) => (prev + 1) % carouselImages.length)}
              >
                <ArrowForward />
              </IconButton>
            </Box>
            <Typography
              variant="h2"
              sx={{ fontWeight: "bold", mb: 3, fontSize: { xs: "2.5rem", md: "4rem" }, color: theme.palette.text.primary }}
            >
              Seamless QR Dining for Your Restaurant
            </Typography>
            <Typography
              variant="h5"
              sx={{ mb: 5, fontWeight: 300, fontSize: { xs: "1.2rem", md: "1.8rem" }, color: theme.palette.text.primary }}
            >
              Scan, order, and pay effortlessly while staff manage tables with real-time precision.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.text.hero,
                  px: 5,
                  py: 1.5,
                  fontSize: "1.2rem",
                  borderRadius: 2,
                  "&:hover": { bgcolor: theme.palette.primary.light },
                }}
                onClick={() => setOpenSignup(true)}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/employee-login"
                sx={{
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.text.secondary,
                  px: 5,
                  py: 1.5,
                  fontSize: "1.2rem",
                  borderRadius: 2,
                  "&:hover": { bgcolor: theme.palette.custom.cardPrimary },
                }}
              >
                Sign In
              </Button>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              mb: 6,
              fontWeight: "bold",
              color: theme.palette.text.primary,
            }}
          >
            Elevate Your Restaurant Operations
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                icon: <RestaurantMenu sx={{ fontSize: 48, color: "#f97316" }} />,
                title: "Touchless Ordering",
                description:
                  "Customers scan QR codes to view menus, place orders, track statuses, and request bills, ensuring a fast and safe experience.",
                image: "https://static.beebom.com/wp-content/uploads/2024/07/how-to-scan-qr-code-on-android.jpg?w=1250&quality=75",
              },
              {
                icon: <Notifications sx={{ fontSize: 48, color: "#f97316" }} />,
                title: "Staff Efficiency",
                description:
                  "Real-time notifications for kitchen and servers streamline order processing and table management.",
                image: "https://media.istockphoto.com/id/480391926/photo/chopping-food-ingredients.jpg?s=612x612&w=0&k=20&c=gNaK4pSwkZIW1h4BKQEuGhXRJgbjbdtgmlFF_Bu4uYI=",
              },
              {
                icon: <BarChart sx={{ fontSize: 48, color: "#f97316" }} />,
                title: "Smart Analytics",
                description:
                  "Admins monitor table occupancy, menu performance, and customer feedback with real-time insights.",
                image: "https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg",
              },
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    transition: "transform 0.3s, box-shadow 0.3s",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: theme.palette.custom.cardHoverShadow,
                    },
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={feature.image}
                      alt={feature.title}
                      sx={{ width: "100%", objectFit: "cover", borderRadius: "12px 12px 0 0" }}
                    />
                    <CardContent sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        {feature.icon}
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: "bold", color: theme.palette.text.primary }}
                        >
                          {feature.title}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: theme.palette.text.tertiary }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Box sx={{ bgcolor: theme.palette.background.recommended, py: 5, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background: `ur[](https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80)`,
              backgroundSize: "cover",
            }}
          />
          <Container maxWidth="lg">
            <Typography
              variant="h3"
              sx={{
                textAlign: "center",
                mb: 6,
                fontWeight: "bold",
                color: theme.palette.text.primary,
              }}
            >
              How It Works
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", mb: 2, color: theme.palette.text.primary }}
                >
                  For Customers
                </Typography>
                {[
                  { text: "Scan: Scan the table’s QR code to access the menu.", icon: <RestaurantMenu /> },
                  { text: "Order: Browse, select, and place orders instantly.", icon: <RestaurantMenu /> },
                  { text: "Pay: Track orders and request bills seamlessly.", icon: <RestaurantMenu /> },
                ].map((step, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box sx={{ fontSize: 30, color: "#f97316", mr: 2 }}>{step.icon}</Box>
                    <Typography sx={{ color: theme.palette.text.tertiary }}>
                      <strong>{step.text.split(":")[0]}:</strong> {step.text.split(":")[1]}
                    </Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", mb: 2, color: theme.palette.text.primary }}
                >
                  For Restaurants
                </Typography>
                {[
                  { text: "Sign Up: Create an account and set up your menu.", icon: <BarChart /> },
                  { text: "Manage: Assign tables and monitor orders in real-time.", icon: <BarChart /> },
                  { text: "Analyze: Gain insights with occupancy and sales data.", icon: <BarChart /> },
                ].map((step, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box sx={{ fontSize: 30, color: "#f97316", mr: 2 }}>{step.icon}</Box>
                    <Typography sx={{ color: theme.palette.text.tertiary }}>
                      <strong>{step.text.split(":")[0]}:</strong> {step.text.split(":")[1]}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box sx={{ bgcolor: theme.palette.background.mostSold, py: 6, textAlign: "center" }}>
          <Container maxWidth="md">
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", mb: 3, color: theme.palette.text.primary }}
            >
              Ready to Modernize Your Restaurant?
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.text.hero,
                "&:hover": { bgcolor: theme.palette.primary.light },
                px: 5,
                py: 1.5,
                fontSize: "1.2rem",
                borderRadius: 2,
              }}
              onClick={() => setOpenSignup(true)}
            >
              Get Started Now
            </Button>
          </Container>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {toasts.map((toast) => (
            <Box
              key={toast.id}
              sx={{
                p: 2,
                bgcolor: toast.variant === "error" ? theme.palette.error.main : theme.palette.custom.buttonPrimary,
                color: theme.palette.text.hero,
                borderRadius: 1,
                boxShadow: theme.palette.custom.cardShadow,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {toast.title}
              </Typography>
              {toast.description && <Typography variant="body2">{toast.description}</Typography>}
            </Box>
          ))}
        </Box>

        <Dialog
          open={openSignup}
          onClose={() => {
            setOpenSignup(false);
            setFormErrors({});
            setFormData({
              companyName: "",
              companyCode: "",
              companyAddress: "",
              companyEmail: "",
              companyPhone: "",
              adminUserId: "",
              adminPassword: "",
            });
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px", bgcolor: theme.palette.background.paper } }}
        >
          <DialogTitle>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
              Start Your Free Trial
            </Typography>
          </DialogTitle>
          <DialogContent>
            {Object.keys(formErrors).length > 0 && (
              <Box sx={{ mb: 3 }}>
                {Object.values(formErrors).map((error, index) => (
                  <Alert key={index} severity="error" sx={{ mb: 1 }}>
                    {error}
                  </Alert>
                ))}
              </Box>
            )}
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  error={!!formErrors.companyName}
                  helperText={formErrors.companyName || "Enter your business name (e.g., Coastal Kitchen)"}
                  sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                />
                <TextField
                  fullWidth
                  label="Business Code"
                  name="companyCode"
                  value={formData.companyCode}
                  onChange={handleChange}
                  required
                  disabled
                  error={!!formErrors.companyCode}
                  helperText={formErrors.companyCode || "Auto-generated unique code (e.g., COA001)"}
                  sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                />
                <TextField
                  fullWidth
                  label="Address"
                  name="companyAddress"
                  value={formData.companyAddress}
                  onChange={handleChange}
                  required
                  multiline
                  rows={3}
                  error={!!formErrors.companyAddress}
                  helperText={formErrors.companyAddress || ""}
                  sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="companyEmail"
                  value={formData.companyEmail}
                  onChange={handleChange}
                  required
                  type="email"
                  error={!!formErrors.companyEmail}
                  helperText={formErrors.companyEmail || ""}
                  sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={handlePhoneInput}
                  required
                  type="text"
                  inputProps={{ maxLength: 10 }}
                  error={!!formErrors.companyPhone}
                  helperText={formErrors.companyPhone || "Enter exactly 10 digits (e.g., 1234567890)"}
                  sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    value={formData.companyCode ? `${formData.companyCode}#` : ""}
                    label="Admin User ID Prefix"
                    variant="outlined"
                    disabled
                    sx={{ width: "30%", borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                    helperText="Business Code Prefix"
                  />
                  <TextField
                    fullWidth
                    label="Admin User ID"
                    name="adminUserId"
                    value={formData.adminUserId}
                    onChange={handleChange}
                    required
                    error={!!formErrors.adminUserId}
                    helperText={formErrors.adminUserId || "Enter the user ID without the prefix"}
                    sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Password"
                  name="adminPassword"
                  value={formData.adminPassword}
                  onChange={handleChange}
                  required
                  type="password"
                  error={!!formErrors.adminPassword}
                  helperText={formErrors.adminPassword || ""}
                  sx={{ borderRadius: "8px", "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: theme.palette.text.secondary } } }}
                />
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setOpenSignup(false);
                      setFormErrors({});
                      setFormData({
                        companyName: "",
                        companyCode: "",
                        companyAddress: "",
                        companyEmail: "",
                        companyPhone: "",
                        adminUserId: "",
                        adminPassword: "",
                      });
                    }}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      px: 4,
                      py: 1,
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.text.secondary,
                      "&:hover": { bgcolor: theme.palette.custom.cardPrimary },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                      bgcolor: theme.palette.custom.buttonPrimary,
                      color: theme.palette.text.hero,
                      "&:hover": { bgcolor: theme.palette.custom.buttonHover },
                      px: 4,
                      py: 1,
                      borderRadius: "8px",
                    }}
                  >
                    {loading ? "Creating Account..." : "Start Free Trial"}
                  </Button>
                </Box>
              </Box>
            </form>
          </DialogContent>
        </Dialog>

        <style jsx>{`
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Box>
    </ThemeProvider>
  );
};

export default LandingPage;